/**
 * De dónde llegó la persona: se anota al cargar la página y se guarda hasta que inicie
 * sesión, porque el login puede pasar varias pantallas (o días) después.
 *
 * Señales, de la más explícita a la menos:
 *   1. `utm_source` (la pone quien arma la campaña). Si `utm_medium` dice que fue un
 *      correo (`email`, `newsletter`…), gana «email» aunque `utm_source` traiga el
 *      nombre del proveedor o de la lista: así lo marcan Mailchimp, Brevo y compañía.
 *   2. Identificadores de clic que agregan las plataformas al link: `igsh` (Instagram),
 *      `fbclid` (Facebook e Instagram), `ttclid` (TikTok), `gclid` (Google Ads)…
 *      Solo se mira que estén; su VALOR es un identificador y no se guarda.
 *   3. `document.referrer`: el sitio de origen (m.facebook.com, l.instagram.com, un
 *      webmail) o, en Chrome para Android, la app que abrió el link
 *      (`android-app://com.whatsapp/`, la app de Gmail).
 *
 * Límite del correo: Apple Mail, Outlook de escritorio y Gmail en iPhone abren el link
 * sin decir de dónde viene, así que esas visitas caen en «Directo o sin dato». La única
 * forma segura de ver una campaña de correo es que sus links lleven UTM.
 *
 * Se guarda solo el NOMBRE del origen (instagram, whatsapp…) o, si no es una plataforma
 * conocida, el dominio. Viaja al backend dentro de `X-Client-UA` como
 * ` goliiive-src/<origen>`, y report lo usa en «De dónde llegan».
 *
 * La página de una pasarela de pago (Stripe Checkout) no es un origen: quien paga sale
 * a ella y vuelve, y sin este filtro la vuelta pisaba el origen real (Instagram…) por
 * siete días. Se ignora al llegar y también si ya había quedado guardada.
 */

const STORAGE_KEY = 'goliiive-entry-source';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_SOURCE_LENGTH = 40;

/** Identificadores de clic, en orden de prioridad (Instagram antes que el fbclid compartido). */
const CLICK_ID_SOURCES: readonly (readonly [string, string])[] = [
  ['igsh', 'instagram'],
  ['igshid', 'instagram'],
  ['ttclid', 'tiktok'],
  ['gclid', 'google_ads'],
  ['gbraid', 'google_ads'],
  ['wbraid', 'google_ads'],
  ['twclid', 'x'],
  ['li_fat_id', 'linkedin'],
  ['fbclid', 'facebook'],
];

/** Alias habituales de `utm_source`. Lo que no está acá se guarda tal cual, saneado. */
const UTM_ALIASES: Record<string, string> = {
  ig: 'instagram',
  instagram: 'instagram',
  fb: 'facebook',
  facebook: 'facebook',
  meta: 'facebook',
  messenger: 'messenger',
  wa: 'whatsapp',
  whatsapp: 'whatsapp',
  tt: 'tiktok',
  tiktok: 'tiktok',
  google: 'google',
  telegram: 'telegram',
  twitter: 'x',
  x: 'x',
  email: 'email',
  mail: 'email',
  correo: 'email',
  newsletter: 'email',
  gmail: 'email',
  outlook: 'email',
  // Proveedores de campañas: su `utm_source` por defecto es su propio nombre.
  mailchimp: 'email',
  brevo: 'email',
  sendinblue: 'email',
  hubspot: 'email',
  klaviyo: 'email',
  mailerlite: 'email',
  activecampaign: 'email',
  sendgrid: 'email',
  constantcontact: 'email',
};

/** Valores de `utm_medium` que dicen «esto llegó por correo». */
const EMAIL_MEDIUMS = new Set(['email', 'e-mail', 'mail', 'correo', 'newsletter']);

/**
 * Dominios con los que los servicios de correo masivo cuentan los clics (Brevo
 * `5uq8q.r.a.d.sendibm1.com`, Mailchimp `*.list-manage.com`, SendGrid, Klaviyo…): el link
 * del correo pasa por ahí y a veces queda como sitio de origen. Es un correo de campaña.
 * Report reconoce los mismos (`ua-classifier.util.ts`) para lo guardado antes.
 */
const EMAIL_CLICK_TRACKER_HOST =
  /(^|\.)(sendib[a-z]*\d*\.com|list-manage\.com|sendgrid\.net|klclick\d*\.com|mlsend\.com|hubspotlinks\.com|acemln[a-z]\.com|rs6\.net)$/;

/** Dominio de origen → plataforma. El correo va antes que Google: `mail.google.com` es Gmail. */
const REFERRER_HOSTS: readonly (readonly [RegExp, string])[] = [
  [/^(web)?mail\./, 'email'],
  [EMAIL_CLICK_TRACKER_HOST, 'email'],
  [/(^|\.)outlook\.(live|office|office365)\.com$/, 'email'],
  [/(^|\.)(protonmail\.com|proton\.me)$/, 'email'],
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)messenger\.com$/, 'messenger'],
  [/(^|\.)(facebook\.com|fb\.com|fb\.me)$/, 'facebook'],
  [/(^|\.)tiktok\.com$/, 'tiktok'],
  [/(^|\.)(whatsapp\.com|wa\.me)$/, 'whatsapp'],
  [/(^|\.)(t\.me|telegram\.org)$/, 'telegram'],
  [/(^|\.)(x\.com|twitter\.com|t\.co)$/, 'x'],
  [/(^|\.)(linkedin\.com|lnkd\.in)$/, 'linkedin'],
  [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube'],
  [/(^|\.)google\.[a-z.]+$/, 'google'],
];

/** Pasarelas de pago a las que se sale a pagar y de las que se vuelve (hoy Stripe Checkout). */
const PAYMENT_RETURN_HOST = /(^|\.)stripe\.com$/;

/** ¿El origen es la vuelta de una pasarela de pago? Entonces no es un origen. */
export function isPaymentReturnSource(source: string | null | undefined): boolean {
  return !!source && PAYMENT_RETURN_HOST.test(source.trim().toLowerCase());
}

/** App de Android que abrió el link (Chrome lo informa como `android-app://<paquete>/`). */
const ANDROID_APPS: Record<string, string> = {
  'com.whatsapp': 'whatsapp',
  'com.whatsapp.w4b': 'whatsapp',
  'org.telegram.messenger': 'telegram',
  'com.instagram.android': 'instagram',
  'com.facebook.katana': 'facebook',
  'com.facebook.orca': 'messenger',
  'com.zhiliaoapp.musically': 'tiktok',
  'com.ss.android.ugc.trill': 'tiktok',
  'com.google.android.gm': 'email',
  'com.google.android.gm.lite': 'email',
  'com.microsoft.office.outlook': 'email',
  'com.yahoo.mobile.client.android.mail': 'email',
  'com.samsung.android.email.provider': 'email',
  'me.proton.android.mail': 'email',
  'com.google.android.googlequicksearchbox': 'google',
  'com.twitter.android': 'x',
  'com.linkedin.android': 'linkedin',
};

/**
 * Los cinco UTM de la página por la que se entró, saneados: viajan con el origen y core
 * los guarda con la compra («Fuente, medio y compradores» de Audiencia/Marketing).
 */
export interface EntryUtm {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
}

interface StoredSource {
  source: string;
  at: number;
  /** Los UTM de la misma entrada; se pisan junto con el origen. */
  utm?: EntryUtm;
}

/** Tope de cada UTM: una campaña con nombre largo se recorta, no se pierde. */
const MAX_UTM_LENGTH = 60;
const UTM_KEYS: readonly (readonly [keyof EntryUtm, string])[] = [
  ['source', 'utm_source'],
  ['medium', 'utm_medium'],
  ['campaign', 'utm_campaign'],
  ['term', 'utm_term'],
  ['content', 'utm_content'],
];

/**
 * Un UTM tal como lo puede llevar la cabecera: sin tildes, minúsculas, los espacios como
 * guiones y solo `[a-z0-9._-]`, hasta 60 caracteres. «Preventa Día 1» → `preventa-dia-1`.
 */
export function sanitizeUtmValue(value: string | null | undefined): string | null {
  const clean = String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[\s+]+/g, '-')
    .replace(/[^a-z0-9._-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, MAX_UTM_LENGTH);
  return clean || null;
}

/** Los UTM de esta carga de página, o `null` si no trae ninguno. */
export function detectEntryUtm(ctx: LandingContext): EntryUtm | null {
  let params: URLSearchParams;
  try {
    params = new URL(ctx.href).searchParams;
  } catch {
    return null;
  }
  const utm: EntryUtm = {};
  for (const [key, param] of UTM_KEYS) {
    const value = sanitizeUtmValue(params.get(param));
    if (value) utm[key] = value;
  }
  return Object.keys(utm).length ? utm : null;
}

/** Lo mínimo de `location` / `document` que se usa, para probarlo sin navegador. */
export interface LandingContext {
  href: string;
  host: string;
  referrer: string;
}

/** Minúsculas, solo `[a-z0-9._-]` y como mucho 40 caracteres. `null` si queda vacío. */
export function sanitizeSource(value: string | null | undefined): string | null {
  const clean = String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '')
    .slice(0, MAX_SOURCE_LENGTH);
  return clean || null;
}

function sourceFromReferrer(referrer: string, ownHost: string): string | null {
  if (!referrer) return null;
  if (referrer.startsWith('android-app://')) {
    const pkg = referrer.slice('android-app://'.length).split('/')[0];
    return ANDROID_APPS[pkg] ?? sanitizeSource(pkg);
  }
  let host: string;
  try {
    host = new URL(referrer).host.toLowerCase();
  } catch {
    return null;
  }
  if (!host || host === ownHost.toLowerCase()) return null; // navegación interna
  if (isPaymentReturnSource(host)) return null; // vuelve de pagar: se queda el origen anterior
  for (const [re, source] of REFERRER_HOSTS) if (re.test(host)) return source;
  return sanitizeSource(host.replace(/^www\./, ''));
}

/** Origen de esta carga de página, o `null` si no hay ninguna señal externa. */
export function detectEntrySource(ctx: LandingContext): string | null {
  let params: URLSearchParams;
  try {
    params = new URL(ctx.href).searchParams;
  } catch {
    params = new URLSearchParams();
  }

  const utm = sanitizeSource(params.get('utm_source'));
  if (utm && UTM_ALIASES[utm]) return UTM_ALIASES[utm];
  // Campaña de correo con el proveedor o la lista en utm_source: cuenta como correo.
  const medium = sanitizeSource(params.get('utm_medium'));
  if (medium && EMAIL_MEDIUMS.has(medium)) return 'email';
  if (utm) return utm;

  const fromReferrer = sourceFromReferrer(ctx.referrer, ctx.host);
  const clickId = CLICK_ID_SOURCES.find(([param]) => params.has(param))?.[1] ?? null;
  // Meta agrega `fbclid` también a los links que salen de Instagram: si el sitio de
  // origen dice Instagram, gana Instagram.
  if (clickId === 'facebook' && (fromReferrer === 'instagram' || fromReferrer === 'messenger')) return fromReferrer;
  return clickId ?? fromReferrer;
}

function safeStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Se llama una vez al cargar la app, antes de que el router toque la URL. Si esta carga
 * trae una señal externa (o algún UTM), la guarda con sus UTM: la última entrada gana
 * entera, así una campaña vieja no queda pegada a una visita nueva. Si no, conserva la
 * anterior.
 */
export function captureEntrySource(
  ctx: LandingContext | null = landingFromWindow(),
  storage: Storage | null = safeStorage(),
  now: number = Date.now(),
): string | null {
  const detected = ctx ? detectEntrySource(ctx) : null;
  const utm = ctx ? detectEntryUtm(ctx) : null;
  if ((detected || utm) && storage) {
    try {
      const stored: StoredSource = { source: detected ?? '', at: now, ...(utm ? { utm } : {}) };
      storage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      /* almacenamiento lleno o bloqueado: el origen se pierde, la app sigue igual */
    }
  }
  return detected ?? getEntrySource(storage, now);
}

/** UTM guardados con el origen vigente (7 días), o `null`. */
export function getEntryUtm(storage: Storage | null = safeStorage(), now: number = Date.now()): EntryUtm | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSource>;
    if (typeof parsed.at !== 'number' || now - parsed.at > TTL_MS || !parsed.utm) return null;
    const utm: EntryUtm = {};
    for (const [key] of UTM_KEYS) {
      const value = sanitizeUtmValue(parsed.utm[key]);
      if (value) utm[key] = value;
    }
    return Object.keys(utm).length ? utm : null;
  } catch {
    return null;
  }
}

/** Origen guardado y vigente (7 días), o `null`. */
export function getEntrySource(storage: Storage | null = safeStorage(), now: number = Date.now()): string | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSource>;
    if (typeof parsed.at !== 'number' || now - parsed.at > TTL_MS) return null;
    const source = sanitizeSource(parsed.source);
    // Guardado por una versión anterior, que anotaba la vuelta de Stripe como origen.
    return isPaymentReturnSource(source) ? null : source;
  } catch {
    return null;
  }
}

/** Tope que acepta core para `X-Client-UA`; pasarlo haría descartar la cabecera entera. */
const MAX_HEADER_LENGTH = 512;

/** Clave corta de cada UTM en la cabecera. */
const UTM_HEADER_KEYS: Record<keyof EntryUtm, string> = { source: 's', medium: 'm', campaign: 'c', term: 't', content: 'n' };

/**
 * Agrega al User-Agent que se reporta ` goliiive-utm/s:…~m:…~c:…` (si hay UTM) y
 * ` goliiive-src/<origen>` (si hay origen). El del origen va SIEMPRE último: core y
 * report lo buscan al final, y uno anterior a los UTM lo sigue encontrando.
 */
export function withEntrySource(ua: string, source: string | null, utm?: EntryUtm | null): string {
  const clean = sanitizeSource(source);
  const pares = utm
    ? (Object.keys(UTM_HEADER_KEYS) as (keyof EntryUtm)[])
        .map((key) => [UTM_HEADER_KEYS[key], sanitizeUtmValue(utm[key])] as const)
        .filter(([, value]) => !!value)
        .map(([key, value]) => `${key}:${value}`)
    : [];
  const tokens = `${pares.length ? ` goliiive-utm/${pares.join('~')}` : ''}${clean ? ` goliiive-src/${clean}` : ''}`;
  if (!ua || !tokens) return ua;
  return `${ua.slice(0, MAX_HEADER_LENGTH - tokens.length).trimEnd()}${tokens}`;
}

function landingFromWindow(): LandingContext | null {
  try {
    return { href: location.href, host: location.host, referrer: document.referrer ?? '' };
  } catch {
    return null;
  }
}
